import {
  ButtonType,
  CheckBoxWidget,
  DialogFooterButtonsWidget,
  DialogPositioningBehavior,
  DialogProps,
  DialogType,
  DialogWidget,
  TextFieldWidget
} from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { cssClass } from '~src/style';

import { PublishedConnectionTaskTableInfo } from '../../types/PublishedConnectionTaskTableInfo';

const cssPrefix = 'PublishedConnectionScheduledTaskTableSelectionDialog';

const fieldStyle: React.CSSProperties = {
  width: '100%',
};

const dialogBodyStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: 250
};

const tablesContainerClass = cssClass(cssPrefix, 'fieldContainer', {
  flex: 1,
  overflow: 'auto',
  paddingTop: CommonStyles.Sizes.DefaultSpace,
});

const rowContainerClass = cssClass(cssPrefix, 'rowContainer', {
  display: 'flex',
});

const checkboxWrapperClass = cssClass(cssPrefix, 'checkboxWrapper', {
  alignItems: 'center',
  flex: 'none',
  height: '100%',
  paddingRight: CommonStyles.Sizes.DefaultSpace
});

const tableNameClass = cssClass(cssPrefix, 'tableName', {
  flex: 1,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

function renderTableRow(
  table: PublishedConnectionTaskTableInfo,
  selected: boolean,
  onChange: (checked: boolean, tableLuid: string) => void
): JSX.Element {
  const handleSelect = (checked: boolean) => onChange(checked, table.luid);

  return (
    <div className={rowContainerClass} key={table.luid}>
      <div className={checkboxWrapperClass}>
        <CheckBoxWidget
          checked={selected}
          handleChange={handleSelect}
          testId={'published-connection-run-schedule-item-' + table.alias}
          removeLeftMargin={true}
          id={table.luid}
        />
      </div>
      <label className={tableNameClass} htmlFor={table.luid} title={table.alias}>{table.alias}</label>
    </div>
  );
}

function getTableSelectionDialogFooterContent(
  cancelTableSelection: () => void,
  handleTableSelection: () => void,
  okEnabled: boolean,
): JSX.Element {

  return (
    <DialogFooterButtonsWidget
      buttons=
        {[
          {
            handleClick: cancelTableSelection,
            buttonType: ButtonType.Outline,
            children: Messages.cancel(),
            testId: 'published-connection-scheduled-task-table-selection-dialog-cancel',
          },
          {
            handleClick: handleTableSelection,
            buttonType: ButtonType.Primary,
            disabled: !okEnabled,
            children: Messages.ok(),
            testId: 'published-connection-scheduled-task-table-selection-dialog-confirm',
          }
        ]}
      testId='published-connection-scheduled-task-table-selection-dialog-footer'
    />
  );
}

export type PublishedConnectionTaskTablesMap = { readonly [id: string]: string };

export type PublishedConnectionScheduledTaskTableSelectionDialogProps = Readonly<{
  anchorElementRef: HTMLElement;
  availableTables: ReadonlyArray<PublishedConnectionTaskTableInfo>;
  onCancel: () => void;
  onSelect: (selectedTables: ReadonlyArray<string>) => void;
  selectedTableLuids: ReadonlyArray<string>;
}>;

export function PublishedConnectionScheduledTaskTableSelectionDialog(
  props: PublishedConnectionScheduledTaskTableSelectionDialogProps
): JSX.Element {
  const { anchorElementRef, availableTables, onCancel, onSelect, selectedTableLuids } = props;
  const initialSelectionState = React.useMemo(
    () => {
      const selected: { [taskId: string]: string } = {};
      selectedTableLuids.forEach(tableLuid => selected[tableLuid] = tableLuid);
      return selected;
    },
    [selectedTableLuids]
  );

  const [currentSelection, setCurrentSelection] = React.useState<PublishedConnectionTaskTablesMap>(initialSelectionState);
  const [searchText, setSearchText] = React.useState('');

  const handleTableSelection = React.useCallback(
    () => {
      const tables = Object.keys(currentSelection);
      onSelect(tables);
    },
    [currentSelection, onSelect]
  );

  const dialogProps: DialogProps = React.useMemo(
    () => ({
      anchorEl: anchorElementRef,
      footerContent: getTableSelectionDialogFooterContent(onCancel, handleTableSelection, Object.keys(currentSelection).length > 0),
      dialogBodyStyle: dialogBodyStyle,
      dialogWidth: 300,
      isModal: false,
      onCloseRequest: onCancel,
      positioningBehavior: DialogPositioningBehavior.Anchored,
      shouldReturnFocusOnClose: false,
      showFooter: true,
      showCloseButton: false,
      testId: 'published-connection-scheduled-task-table-selection-dialog',
      title: Messages.publishedConnectionScheduledTaskAddEditTables(),
      type: DialogType.Uber,
    }),
    [anchorElementRef, handleTableSelection, onCancel, currentSelection]
  );

  const handleInputChange = React.useCallback((e) => setSearchText(e.target.value), [setSearchText]);

  const clearInput = React.useCallback(() => setSearchText(''), [setSearchText]);

  const handleSelectTable = React.useCallback((checked: boolean, tableLuid: string) => {
      if (checked) {
        setCurrentSelection({
          ...currentSelection,
          [tableLuid]: tableLuid
        });
      } else {
        const { [tableLuid]: removed, ...rest } = currentSelection;
        setCurrentSelection(rest);
      }
    },
    [currentSelection, setCurrentSelection]
  );

  const filteredTables = React.useMemo(
    (): ReadonlyArray<PublishedConnectionTaskTableInfo> => {
      if (searchText) {
        const searchTextUpperCase = searchText.toUpperCase();
        return availableTables.filter(table => table.alias.toUpperCase().indexOf(searchTextUpperCase) >= 0);
      }
      return availableTables;
    },
    [searchText, availableTables]
  );

  return (
    <DialogWidget {...dialogProps}>
      <TextFieldWidget
        kind='search'
        label={undefined}
        onChange={handleInputChange}
        onClear={clearInput}
        placeholder={Messages.find()}
        role='combobox'
        style={fieldStyle}
        testId={'published-connection-scheduled-task-table-selection-dialog-filter'}
        value={searchText}
      />
      <div className={tablesContainerClass}>
        {filteredTables.map(table => renderTableRow(table, !!currentSelection[table.luid], handleSelectTable))}
      </div>
    </DialogWidget>
  );
}
